import { memo, useMemo, useRef, useState } from 'react';
import { useStoreMap } from 'effector-react';
import { type XYCoord, useDrop } from 'react-dnd';
import { type GeneralID, ImageType, type SceneData, SceneType } from '@types';
import { $sceneMap } from '@stores/dataStores';
import { CheckboxNoFunc } from '@components/Checkbox';
import Image from '@components/Image';
import TextLink from '@components/TextLink';
import cn from '@utils/cn';
import { dragScenes } from '@modules/category/actions/dragActions';
import { changeThumb } from '@modules/category/actions/changeThumb';
import RenameSceneModal from '@modules/category/SceneList/RenameSceneModal';
import { renameScene } from '@modules/category/actions/renameScene';
import addComponent from '@utils/addComponent';
import duplicateScene from '@modules/category/actions/duplicateScene';
import replaceScene from '@modules/category/actions/replaceScene';
import { showConfirmModal } from '@components/ConfirmModal';
import { deleteScene } from '@modules/category/actions/deleteActions';
import ImgPano from './img-pano.png';
import ImgPVideo from './img-pvideo.png';
import ImgMatrix from './img-matrix.png';
import { $scenesManagement, $scenesManagementApi } from '../stores';
import { SMDndType, type SMDragItem, isScenesItem } from '../dnd';

interface Props {
  id: GeneralID;
  categoryId: number;
  isDragging: boolean;
  onActive: (id: GeneralID) => void;
}

export const Item = memo(function Item({
  id,
  categoryId,
  isDragging,
  onActive,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const data = useStoreMap({
    store: $sceneMap,
    keys: [id],
    fn: (state, [_id]) => state.get(_id) || ({} as SceneData),
  });
  const isSelected = useStoreMap({
    store: $scenesManagement,
    keys: [id],
    fn: (state, [_id]) => state.selected.includes(_id),
  });

  const markSrc = useMemo(() => {
    if (data.type === SceneType.pano) {
      return ImgPano;
    }
    if (data.type === SceneType.panoVideo) {
      return ImgPVideo;
    }
    if (data.type === SceneType.matrix) {
      return ImgMatrix;
    }
    return null;
  }, [data.type]);

  const [hoverPos, setHoverPos] = useState<null | 'top' | 'bottom'>(null);

  const [{ isOver }, drop] = useDrop({
    accept: SMDndType.scenes,
    hover(item: SMDragItem, monitor) {
      if (isScenesItem(item, monitor.getItemType())) {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const middleY = (rect.bottom - rect.top) / 2;
        const clientOffset = monitor.getClientOffset() as XYCoord;
        const hoverY = clientOffset.y - rect.top;
        if (hoverY < middleY) {
          setHoverPos('top');
        } else {
          setHoverPos('bottom');
        }
      }
    },
    collect: (monitor) => {
      return {
        isOver: monitor.isOver(),
      };
    },
    drop: (item: SMDragItem, monitor) => {
      if (isScenesItem(item, monitor.getItemType())) {
        dragScenes({
          categoryId,
          ids: item.ids,
          targetId: id,
          pos: hoverPos || 'bottom',
        });
      }
    },
  });

  drop(ref);

  const handleRename = async () => {
    addComponent((remove) => (
      <RenameSceneModal
        isOpen
        oldName={data.name}
        onRequestClose={remove}
        onConfirm={(result) => {
          renameScene({
            sceneId: id,
            name: result.name,
            renameHotspot: result.renameHotspot,
            renameSandTable: result.renameSandTable,
          });
          remove();
        }}
      />
    ));
  };

  const handleDelete = async () => {
    const { confirmed } = await showConfirmModal({
      title: '确认删除场景',
      content: `场景${data.name}下所有设置也都将会被删除，确认删除？`,
    });
    if (confirmed) {
      deleteScene([{ sceneId: id, categoryId }]);
      $scenesManagementApi.update((draft) => {
        draft.selected = draft.selected.filter((_id) => _id !== id);
      });
    }
  };

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex h-50 justify-between gap-100 border-t border-b border-t-[#fff] p-20 hover:border-t-[#f8f8f8]',
        {
          'hover:bg-[#f8f8f8]': !isDragging,
        }
      )}
      onPointerDown={(e) => {
        e.stopPropagation();
        onActive(id);
      }}
      onClick={(e) => {
        e.stopPropagation();
        $scenesManagementApi.update((draft) => {
          if (isSelected) {
            draft.selected = draft.selected.filter((_id) => _id !== id);
          } else {
            draft.selected.push(id);
            draft.selected.sort(
              (a, b) => draft.sceneList.indexOf(a) - draft.sceneList.indexOf(b)
            );
          }
        });
      }}
    >
      <div className="flex min-w-0 items-center gap-10">
        <CheckboxNoFunc checked={isSelected} />
        <Image
          className="h-32 w-32 shrink-0"
          src={data.thumbUrl}
          type={ImageType.thumb}
          size={32}
        />
        <div className="truncate" title={data.name}>
          {data.name}
        </div>
        {markSrc && <img src={markSrc} className="h-[16px] w-[26px]" alt="" />}
      </div>
      <div className="flex shrink-0 items-center gap-20 text-sm">
        <TextLink
          onClick={async (e) => {
            e.stopPropagation();
            await changeThumb(data);
          }}
        >
          修改封面
        </TextLink>
        <TextLink
          onClick={(e) => {
            e.stopPropagation();
            handleRename();
          }}
        >
          重命名
        </TextLink>
        <TextLink
          onClick={(e) => {
            e.stopPropagation();
            duplicateScene({ sceneId: id, categoryId });
          }}
        >
          复制
        </TextLink>
        <TextLink
          onClick={async (e) => {
            e.stopPropagation();
            await replaceScene({ sceneType: data.type, id: data.id });
          }}
        >
          替换
        </TextLink>
        <TextLink
          onClick={async (e) => {
            e.stopPropagation();
            await handleDelete();
          }}
        >
          删除
        </TextLink>
      </div>
      {isOver && hoverPos === 'top' && (
        <div className="absolute top-0 left-0 right-0 z-10 h-4 bg-green/50" />
      )}
      {isOver && hoverPos === 'bottom' && (
        <div className="absolute -bottom-[6px] left-0 right-0 z-10 h-4 bg-green/50" />
      )}
    </div>
  );
});

export default Item;
